import { useQuery } from "@tanstack/react-query";
import React from "react";
import { PlanDocumentsList } from "@/components/plans/PlanDocuments";
import { AlertTriangle, Edit3 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { PermissionButton } from "@/components/ui/permission-button";
import { EnrollmentFormDownload } from "@/components/enrollment/EnrollmentFormDownload";
import { EnrollmentBadges } from "@/components/ui/benefits/EnrollmentBadges";
import { isCurrentOrUpcomingDependentEnrollmentForPlan, isPendingEnrollment, computeEnrollmentPremiumResult, determineCoverageTierFromDependents } from "@/lib/enrollmentFilters";
import { usePlanPremiumData } from "@/hooks/usePlanPremiumData";
import { isLifeDisabilityType } from "@shared/schemas/plans";
import { isFsaPlanType } from "@shared/schemas/planTypes";
import { getPlanTypeAccent } from "@shared/schemas/planTypes";
import { formatBenefitAmount } from "@shared/utils/enrollmentCalculator";
import {
  hasNonMonthlyPayCycle,
  calculatePerPaycheckAmount,
  getPayCycleLabel,
  calculateAnnualDeductionAmount,
} from "@/lib/payrollUtils";
import { formatDateDisplay } from "@/lib/utils";
import { getTypeIcon } from "./typeIcons";
import { NeedsConfigPill } from "./NeedsConfigPill";
import { resolveNeedsConfigReason } from "./needsConfig";
import { PremiumBreakdownPopover } from "./PremiumBreakdownPopover";
import { isUnpricedPremium } from "@/lib/planCardPriceState";
import { amountOrPlaceholder } from "@/lib/loadingPlaceholder";
import { ratingAreaGapPriceMessage } from "@/lib/planPriceDisplay";

function formatUSD(n: number | string | null | undefined) {
  const v = typeof n === "number" ? n : parseFloat(String(n ?? 0));
  if (!Number.isFinite(v)) return "$0.00";
  return v.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

function initialsOf(first?: string | null, last?: string | null) {
  const f = (first || "").trim();
  const l = (last || "").trim();
  return ((f[0] || "") + (l[0] || "")).toUpperCase() || "?";
}

interface ActiveBenefitRowProps {
  benefit: any;
  employee: any;
  dependents: any[];
  employeeBenefits: any[];
  onModify: () => void;
  /** When true, the Modify button is hidden. Used by the employee /benefits read-only view. */
  readOnly?: boolean;
  /**
   * When true, the employee's covered-member pill renders with a " · you"
   * suffix. Defaults to false because broker/employer admins viewing an
   * employee's page are NOT the employee.
   */
  viewerIsEmployee?: boolean;
  /**
   * Whether the premium-breakdown popover may be shown to an employee viewer.
   * Gated by the employer's benefitsDisplaySettings; only consulted when
   * viewerIsEmployee. Broker/employer views always see the breakdown.
   */
  allowBreakdown?: boolean;
}

export function ActiveBenefitRow({
  benefit,
  employee,
  dependents,
  employeeBenefits,
  onModify,
  readOnly = false,
  viewerIsEmployee = false,
  allowBreakdown = true,
}: ActiveBenefitRowProps) {
  const plan = benefit?.plan;
  const planId = plan?.id as number | undefined;
  const clientId = employee?.clientId;
  const planType = String(plan?.type || "");
  const Icon = getTypeIcon(planType);
  const isLD = isLifeDisabilityType(planType);
  const isFsa = isFsaPlanType(planType);
  const annualElection = (benefit as any)?.annualElection != null
    ? Number((benefit as any).annualElection)
    : null;
  const accent = getPlanTypeAccent(planType);
  const pending = isPendingEnrollment(benefit);
  const stateStripeClass = pending ? "border-l-blue-500" : "border-l-emerald-500";

  // Premium data (rates + contributions for this plan AND any referenced base
  // plans) via the shared loader, so buy-up employer contributions resolve and
  // the whole-table contributions payload is cached app-wide. Drives only the
  // breakdown popover recompute below — the headline prefers the server /
  // portal premium (see `monthlyEE`).
  const premiumData = usePlanPremiumData(planId ? [planId] : [], clientId);
  const planRates = React.useMemo(
    () => (planId ? premiumData.ratesByPlanId[planId] || [] : []),
    [planId, premiumData.ratesByPlanId],
  );
  const allContributions = premiumData.allPlanContributions;
  const allPlanRates = premiumData.allPlanRates;
  const contribsFetched = !premiumData.isLoading;

  const coveredDependents = React.useMemo(() => {
    if (!planId) return [] as any[];
    const enrolledDepIds = (employeeBenefits || [])
      .filter((r: any) => isCurrentOrUpcomingDependentEnrollmentForPlan(r, planId))
      .map((r: any) => r.dependentId as number);
    return (dependents || []).filter((d: any) => enrolledDepIds.includes(d.id as number));
  }, [planId, employeeBenefits, dependents]);

  // Derive the tier label from the actually-covered set — the SAME source the
  // pills and premium use — never the stored `benefit.coverageTier`, which is a
  // denormalized cache that drifts out of sync with the dependent rows.
  const coverageTierLabel = React.useMemo(
    () =>
      determineCoverageTierFromDependents(
        coveredDependents,
        coveredDependents.map((d: any) => d.id as number),
      ).replace(/_/g, " "),
    [coveredDependents],
  );

  const planContribs = React.useMemo(() => {
    if (!planId) return [] as any[];
    return (allContributions || []).filter((c: any) => c.planId === planId);
  }, [planId, allContributions]);

  const { data: ldConfig } = useQuery({
    queryKey: ["/api/plans", planId, "life-disability-config"],
    queryFn: async () => {
      const res = await fetch(`/api/plans/${planId}/life-disability-config`);
      if (!res.ok) return null;
      return res.json();
    },
    enabled: !!planId && isLD,
    staleTime: Infinity,
  });

  // Age-rate at the CLIENT's plan year, not the library row's dates: the
  // server attaches `clientPlanYear` (the client_plans link containing this
  // enrollment's effective date, withComputedPremiums) so "Plan Anniversary"
  // anchors on e.g. 11/1 for a library plan dated 1/1. Library dates only
  // when the plan isn't linked.
  const clientPlanYear = benefit?.clientPlanYear;
  const ratingPlan = React.useMemo(
    () => (plan && clientPlanYear ? { ...plan, ...clientPlanYear } : plan),
    [plan, clientPlanYear],
  );
  const premiumResult = React.useMemo(
    () =>
      computeEnrollmentPremiumResult({
        plan: ratingPlan,
        planRates: planRates as any[],
        planContributions: planContribs,
        allPlanContributions: allContributions,
        allPlanRates: allPlanRates,
        employee,
        dependents: coveredDependents,
        effectiveDate: benefit?.effectiveDate,
        ldConfig: isLD ? ldConfig : null,
      }),
    [ratingPlan, planRates, planContribs, allContributions, allPlanRates, employee, coveredDependents, benefit?.effectiveDate, isLD, ldConfig],
  );

  const needsConfigReason = resolveNeedsConfigReason({
    loaded: contribsFetched && !!planId,
    isFsa,
    isLifeDisability: isLD,
    contributionCount: planContribs.length,
    coverageAmountResolved:
      premiumResult != null ? premiumResult.benefitAmount != null : null,
  });

  // The headline is the amount payroll deducts: the server-computed premium
  // on the enrollment (withComputedPremiums — the same number the Deduction
  // Summary totals and the payroll push sends). The in-browser recompute used
  // to win here "so the headline matches the popover", but it can't resolve
  // the client's eligibility-group contributions and disagreed with the
  // deduction summary on the same page ($143.61 vs $147.71 for one
  // enrollment). It is now the fallback for rows the server didn't price,
  // and the popover is told the server's split so the two agree.
  const monthlyEE = (() => {
    const precomputed = Number(benefit.employeePremium ?? benefit.monthlyPremium);
    const fromCalc = Number(premiumResult?.employeePremium);
    if (Number.isFinite(precomputed)) return precomputed;
    if (isFsa) return 0;
    return Number.isFinite(fromCalc) ? fromCalc : 0;
  })();
  const serverPriced = Number.isFinite(Number(benefit.employeePremium ?? benefit.monthlyPremium));
  // No server price yet AND rates/contributions still in flight: the headline
  // would be the fallback's 0 → "$0.00". Show a dash until the number is real
  // (BUG-008). Server-priced rows never wait on the recompute.
  const priceLoading = !serverPriced && !isFsa && premiumData.isLoading;
  const popoverResult = React.useMemo(() => {
    if (!premiumResult || !serverPriced) return premiumResult;
    const employer = Number(benefit.employerPremium);
    const total = Number(benefit.totalPremium);
    return {
      ...premiumResult,
      employeePremium: monthlyEE,
      employerPremium: Number.isFinite(employer) ? employer : premiumResult.employerPremium,
      totalMonthlyRate: Number.isFinite(total) ? total : premiumResult.totalMonthlyRate,
    };
  }, [premiumResult, serverPriced, benefit.employerPremium, benefit.totalPremium, monthlyEE]);
  const isNonMonthly = hasNonMonthlyPayCycle(employee?.payCycle);
  const perCycle = isNonMonthly ? calculatePerPaycheckAmount(monthlyEE, employee?.payCycle) : monthlyEE;
  // DEV-382: rates exist, but none for the group's rating area → the price is
  // a meaningless $0. The server flags it on the enrollment; the browser loader
  // covers rows the server didn't price.
  const cantPriceMessage = isFsa
    ? null
    : ratingAreaGapPriceMessage(
        benefit?.ratingAreaGap ?? (serverPriced || !planId ? null : premiumData.ratingAreaGapsByPlanId?.[planId]),
        plan?.name,
      );
  // DEV-389: a $0 from missing setup isn't a price. Same verdict as the
  // popover's cannotCalculate; checked before employerPaid.
  const priceUnavailable =
    !priceLoading &&
    !isFsa &&
    !cantPriceMessage &&
    (!!needsConfigReason || isUnpricedPremium(popoverResult ?? { employeePremium: monthlyEE }));
  // Fully employer-paid: employee owes nothing but the plan has a real employer cost.
  const employerPaid = !priceLoading && !isFsa && !cantPriceMessage && monthlyEE === 0 && Number(premiumResult?.employerPremium ?? 0) > 0;
  const breakdownPopover =
    !isFsa && premiumResult && (premiumResult.members.length > 0 || needsConfigReason) && (!viewerIsEmployee || allowBreakdown) ? (
      <PremiumBreakdownPopover
        result={popoverResult}
        payCycle={employee?.payCycle}
        planName={plan?.name}
        coverageTier={coverageTierLabel}
        employee={employee}
        coveredDependents={coveredDependents}
        needsConfig={needsConfigReason}
        isLoading={premiumData.isLoading}
      />
    ) : null;

  return (
    <div className={cn(
      "rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900 p-4 md:p-4",
      "border-l-4",
      stateStripeClass,
    )}>
      {/* Top row */}
      <div className="flex items-start gap-3">
        <div className={cn("h-9 w-9 rounded-lg flex items-center justify-center shrink-0", accent.accentBg)}>
          <Icon className={cn("h-5 w-5", accent.accentText)} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <EnrollmentBadges enrollment={benefit} />
            {needsConfigReason && <NeedsConfigPill reason={needsConfigReason} />}
            {benefit?.effectiveDate && (
              <span className="text-[11px] text-stone-500 dark:text-stone-400 font-mono">
                eff. {formatDateDisplay(benefit.effectiveDate)}
              </span>
            )}
          </div>
          <div className="text-[14.5px] font-semibold text-stone-900 dark:text-stone-100 leading-tight tracking-tight">
            {plan?.name || "Plan"}
          </div>
          <div className="text-xs text-stone-600 dark:text-stone-400 mt-0.5">
            {plan?.carrier || ""}
            {plan?.networkType ? (
              <span className="text-stone-400 dark:text-stone-500"> · {plan.networkType}</span>
            ) : null}
            {isLD && premiumResult?.benefitAmount != null && (
              <span className="text-stone-400 dark:text-stone-500">
                {" · "}
                {formatBenefitAmount(premiumResult.benefitAmount, premiumResult.benefitAmountPeriod)}
              </span>
            )}
          </div>
        </div>
        <div className="text-right shrink-0">
          {cantPriceMessage ? (
            <div
              className="flex items-start gap-1 max-w-[260px] text-left text-xs text-amber-700 dark:text-amber-400"
              data-testid="benefit-row-price-unavailable"
              title={cantPriceMessage}
            >
              <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" />
              <span className="break-words">{cantPriceMessage}</span>
            </div>
          ) : priceUnavailable ? (
            <div className="flex items-center justify-end gap-1.5">
              <div
                className="text-sm font-semibold text-amber-700 dark:text-amber-400"
                data-testid="benefit-row-price-unavailable"
              >
                Price unavailable
              </div>
              {breakdownPopover}
            </div>
          ) : employerPaid ? (
            <>
              <div className="text-sm font-semibold text-stone-900 dark:text-stone-100">Employer paid</div>
              <div className="text-[11px] text-stone-400 dark:text-stone-500">no payroll deduction</div>
            </>
          ) : (
            <>
              <div className="flex items-center justify-end gap-1.5">
                <div
                  className="text-lg font-bold font-mono tracking-tight text-stone-900 dark:text-stone-100"
                  data-testid="benefit-row-per-period"
                  data-loading={priceLoading ? "true" : undefined}
                >
                  {amountOrPlaceholder(priceLoading, () => formatUSD(perCycle))}
                </div>
                {breakdownPopover}
              </div>
              <div className="text-[11px] text-stone-400 dark:text-stone-500">
                / {isNonMonthly ? getPayCycleLabel(employee?.payCycle) : "month"} ·{" "}
                <span className="font-mono">
                  {amountOrPlaceholder(priceLoading, () => formatUSD(calculateAnnualDeductionAmount(monthlyEE, employee?.payCycle)))}
                </span>/yr
              </div>
            </>
          )}
        </div>
      </div>

      {/* Bottom row */}
      <div className="mt-3 pt-3 border-t border-dashed border-stone-200 dark:border-stone-700 flex flex-wrap items-center gap-x-3 gap-y-2 justify-between">
        <div className="flex flex-wrap items-center gap-2 min-w-0">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-stone-500 dark:text-stone-400 mr-1">
            Covered
          </span>
          <CoveredMemberPill
            label={`${employee?.firstName ?? ""} ${employee?.lastName ?? ""}`.trim() || "Employee"}
            initials={initialsOf(employee?.firstName, employee?.lastName)}
            self
            suffix={viewerIsEmployee ? " · you" : undefined}
          />
          {coveredDependents.map((d: any) => (
            <CoveredMemberPill
              key={d.id}
              label={`${d.firstName ?? ""} ${d.lastName ?? ""}`.trim() || d.relationship}
              initials={initialsOf(d.firstName, d.lastName)}
            />
          ))}
          {isFsa && annualElection != null && Number.isFinite(annualElection) ? (
            <span className="text-[11px] text-stone-500 dark:text-stone-400">
              · {formatUSD(annualElection)} annual election
            </span>
          ) : coverageTierLabel && (
            <span className="text-[11px] text-stone-500 dark:text-stone-400">
              · {coverageTierLabel}
            </span>
          )}
        </div>
        {!readOnly && (
          <div className="flex items-center gap-2">
            <EnrollmentFormDownload employeeId={employee?.id} planId={planId} />
            <PermissionButton
              permission="clients:edit"
              whenDenied="hide"
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={onModify}
            >
              <Edit3 className="h-3.5 w-3.5" />
              Modify
            </PermissionButton>
          </div>
        )}
      </div>
      {/* Plan documents (SBC etc.) — downloadable by the member; links render
          as links. Hidden when the plan has none. */}
      {planId != null && (
        <div className="mt-2">
          <PlanDocumentsList planId={planId} compact />
        </div>
      )}
    </div>
  );
}

function CoveredMemberPill({
  label,
  initials,
  self = false,
  suffix,
}: {
  label: string;
  initials: string;
  self?: boolean;
  suffix?: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-1.5 py-0.5 text-[11.5px] border",
        self
          ? "bg-stone-50 border-stone-200 text-stone-700 dark:bg-stone-800 dark:border-stone-700 dark:text-stone-200"
          : "bg-white border-stone-200 text-stone-700 dark:bg-stone-900 dark:border-stone-700 dark:text-stone-200",
      )}
    >
      <Avatar className="h-4 w-4">
        <AvatarFallback
          className={cn(
            "text-[8.5px] font-semibold",
            self
              ? "bg-stone-900 text-white dark:bg-stone-200 dark:text-stone-900"
              : "bg-stone-400 text-white dark:bg-stone-600",
          )}
        >
          {initials}
        </AvatarFallback>
      </Avatar>
      <span>
        {label}
        {suffix && <span className="text-stone-400 dark:text-stone-500">{suffix}</span>}
      </span>
    </span>
  );
}
